import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
} from "react";
import Button from "../../framework/Button";
import Checkbox from "../../framework/Checkbox";
import Aggrid from "../../framework/AgGrid";
import { fetchAdminUsers, saveCACUser } from "../../services/user-service";
import { displayToast } from "../../framework/toast/toastUtils";
import { withDefaultAccess } from "../../utils/accessUtils";
import {
  getAllPrivilegeIds,
  isAdminCacUser,
  isDefaultAccessTab,
  privilege_groups,
  userAccessColDefsForExport,
} from "./constants";
import classes from "../roles/index.module.css";

const CacRoles = () => {
  const [allUsers, setAllUsers] = useState(null);
  const [api, setApi] = useState(null);

  const getEffectiveAccess = useCallback((user) => {
    if (isAdminCacUser(user)) return getAllPrivilegeIds();
    if (user?.access == null) return withDefaultAccess([]);
    const access = Array.isArray(user.access) ? user.access : [];
    return withDefaultAccess(access);
  }, []);

  const refresh = useCallback(() => {
    fetchAdminUsers().then((r) => {
      if (r?.status === "success") {
        const users = (r.result || []).map((user) => {
          if (isAdminCacUser(user)) {
            return { ...user, access: getAllPrivilegeIds() };
          }
          return {
            ...user,
            access: withDefaultAccess(
              Array.isArray(user.access) ? user.access : []
            ),
          };
        });
        setAllUsers(users);
        users.forEach((user) => {
          if (!isAdminCacUser(user)) return;
          saveCACUser({
            _id: user._id,
            username: user.username,
            access: getAllPrivilegeIds(),
          });
        });
      } else {
        displayToast({
          type: "error",
          title: r?.result || "Unable to load CAC users.",
        });
      }
    });
  }, []);

  const saveUserAccess = useCallback((user, access) => {
    if (isAdminCacUser(user)) {
      access = getAllPrivilegeIds();
    } else {
      access = withDefaultAccess(access);
    }
    return saveCACUser({
      _id: user._id,
      username: user.username,
      access,
    }).then((result) => {
      if (!result) {
        displayToast({
          type: "error",
          title: "Unable to update access.",
        });
        return Promise.reject("Unable to update access.");
      }
      return result;
    });
  }, []);

  const updateAllPrivileges = useCallback(
    (e, user, group, index) => {
      if (isAdminCacUser(user)) return;
      let updatedAccess = getEffectiveAccess(user).slice();
      group.privileges.forEach((p) => {
        if (isDefaultAccessTab(p.id)) return;
        if (e.target.checked) {
          if (updatedAccess.findIndex((x) => x === p.id) === -1) {
            updatedAccess = [...updatedAccess, p.id];
          }
        } else {
          updatedAccess = updatedAccess.filter((x) => x !== p.id);
        }
      });
      updatedAccess = withDefaultAccess(updatedAccess);
      saveUserAccess(user, updatedAccess);
      setAllUsers((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], access: updatedAccess };
        return next;
      });
    },
    [getEffectiveAccess, saveUserAccess]
  );

  const privilegeChangeHandler = useCallback(
    (e, user, privilege, index) => {
      if (isAdminCacUser(user) || isDefaultAccessTab(privilege.id)) return;
      let updatedAccess = getEffectiveAccess(user).slice();
      if (updatedAccess.findIndex((p) => p === privilege.id) !== -1) {
        updatedAccess = updatedAccess.filter((x) => x !== privilege.id);
      } else {
        updatedAccess = [...updatedAccess, privilege.id];
      }
      updatedAccess = withDefaultAccess(updatedAccess);
      saveUserAccess(user, updatedAccess);
      setAllUsers((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], access: updatedAccess };
        return next;
      });
    },
    [getEffectiveAccess, saveUserAccess]
  );

  const isGroupToggleChecked = useCallback(
    (user, group) => {
      if (isAdminCacUser(user)) return true;
      const access = getEffectiveAccess(user);
      const editablePrivileges = group.privileges.filter(
        (p) => !isDefaultAccessTab(p.id)
      );
      if (!editablePrivileges.length) return true;
      return editablePrivileges.every((p) => access.includes(p.id));
    },
    [getEffectiveAccess]
  );

  const exportUserAccessData = useCallback(() => {
    api?.exportDataAsCsv({ fileName: "cac-users-roles-report" });
  }, [api]);

  const exportRowData = (allUsers || []).map((user) => ({
    username: user.username,
    company: user.company,
    access: getEffectiveAccess(user),
  }));

  const getTableData = () => {
    return (
      <table>
        <thead>
          <tr>
            <th>CAC Screens</th>
            {allUsers?.map((user) => {
              return (
                <th key={`cac_user_key_${user._id}`}>
                  <div className={classes["roles-app-header-cell"]}>
                    {user.username}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {privilege_groups.map((_pGroups) => {
            return (
              <Fragment key={`privilege_key_${_pGroups.groupId}`}>
                <tr>
                  <td>
                    <div
                      className={`${classes["roles-app-cell"]} ${classes["roles-app-group-header-cell"]}`}
                    >
                      {_pGroups.groupLabel}
                    </div>
                  </td>
                  {allUsers?.map((user, pgRIndex) => {
                    const isAdmin = isAdminCacUser(user);
                    const editablePrivileges = _pGroups.privileges.filter(
                      (p) => !isDefaultAccessTab(p.id)
                    );
                    return (
                      <td key={`user_header_key_${user._id}`}>
                        <div
                          className={`${classes["roles-app-cell"]} ${classes["roles-app-group-header-cell"]}`}
                        >
                          <Checkbox
                            checked={isGroupToggleChecked(user, _pGroups)}
                            disabled={isAdmin || !editablePrivileges.length}
                            onChange={(e) =>
                              updateAllPrivileges(e, user, _pGroups, pgRIndex)
                            }
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {_pGroups.privileges.map((_privileges) => {
                  const isDefault = isDefaultAccessTab(_privileges.id);
                  return (
                    <tr key={`privilege_key_${_privileges.id}`}>
                      <td>
                        <div
                          className={`${classes["roles-app-cell"]} ${classes["privilege-container"]}`}
                        >
                          <div className={classes["roles-app-cell-label"]}>
                            {_privileges.label}
                            {isDefault ? " (Default)" : ""}
                          </div>
                          <div className={classes["roles-app-cell-desc"]}>
                            {_privileges.desc}
                          </div>
                        </div>
                      </td>
                      {allUsers?.map((user, index) => {
                        const isAdmin = isAdminCacUser(user);
                        const access = getEffectiveAccess(user);
                        const locked = isAdmin || isDefault;
                        return (
                          <td key={`user_key_${user._id}`}>
                            <div className={`${classes["roles-app-cell"]}`}>
                              <Checkbox
                                checked={
                                  locked ||
                                  access.indexOf(_privileges.id) !== -1
                                }
                                disabled={locked}
                                onChange={(e) =>
                                  privilegeChangeHandler(
                                    e,
                                    user,
                                    _privileges,
                                    index
                                  )
                                }
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    if (!allUsers) refresh();
  }, [allUsers, refresh]);

  return (
    <div className={classes["roles-app"]}>
      <div className={classes["roles-app-body"]}>{getTableData()}</div>
      <div className={classes["roles-app-footer"]}>
        <Button label="Refresh" onClick={refresh} />
        <Button label="Export" onClick={exportUserAccessData} />
      </div>
      <div style={{ display: "none" }}>
        <Aggrid
          onGridReady={(params) => setApi(params.api)}
          columnDefs={userAccessColDefsForExport}
          rowData={exportRowData}
        />
      </div>
    </div>
  );
};

export default CacRoles;
